import React, { useRef, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../AuthContext';
import { ALL_JOBS_WITH_ADDRESSES_QUERY } from '../graphql/jobs';
import { CREATE_ADDRESS_MUTATION } from '../graphql/customers';
import { CREATE_DRAFT_QUOTE_MUTATION } from '../graphql/draftQuotes';
import AddressPicker, { AddressOption, AddressPickerHandle } from './Address/AddressPicker';
import CloseIcon from '@mui/icons-material/Close';

interface QuoteDialogProps {
  onComplete: (result: { quoteId: number }) => void;
  onClose: () => void;
  initialJobId?: number;
}

interface JobForDialog {
  id: number;
  name: string;
  archivedAt: string | null;
  addressByAddressId: AddressOption | null;
  customerByCustomerId: {
    id: number;
    name: string;
    addressesByCustomerId: {
      nodes: AddressOption[];
    };
  };
}

function dedupeAddresses(addresses: AddressOption[]): AddressOption[] {
  const seen = new Set<number>();
  return addresses.filter((a) => {
    if (seen.has(a.id)) return false;
    seen.add(a.id);
    return true;
  });
}

const dialogOverlayStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const dialogStyle: React.CSSProperties = {
  backgroundColor: 'white', borderRadius: '8px',
  boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)',
  maxWidth: '600px', width: '90%', maxHeight: '90vh',
  display: 'flex', flexDirection: 'column',
};
const dialogHeaderStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '24px', borderBottom: '1px solid #e5e7eb',
};
const dialogContentStyle: React.CSSProperties = { flex: 1, overflowY: 'auto', padding: '24px' };
const dialogFooterStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'flex-end', gap: '12px',
  padding: '16px 24px', borderTop: '1px solid #e5e7eb',
};
const formGroupStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' };
const labelStyle: React.CSSProperties = { fontSize: '13px', fontWeight: 600, color: '#374151' };
const inputStyle: React.CSSProperties = { padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontFamily: 'inherit' };
const textareaStyle: React.CSSProperties = { ...inputStyle, resize: 'vertical', minHeight: '80px' };
const submitButtonStyle: React.CSSProperties = { padding: '10px 24px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };
const cancelButtonStyle: React.CSSProperties = { padding: '10px 24px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };
const errorStyle: React.CSSProperties = { padding: '12px', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '6px', fontSize: '13px', marginBottom: '16px' };

export default function QuoteDialog({ onComplete, onClose, initialJobId }: QuoteDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    jobId: initialJobId ? String(initialJobId) : '' as string | number,
    billToAddressId: '' as number | '',
    shipToAddressId: '' as number | '',
    notes: '',
  });

  const { data: jobsData } = useQuery(ALL_JOBS_WITH_ADDRESSES_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
  });

  const [createDraftQuote] = useMutation(CREATE_DRAFT_QUOTE_MUTATION);
  const [createAddress] = useMutation(CREATE_ADDRESS_MUTATION);
  const billToPickerRef = useRef<AddressPickerHandle>(null);
  const shipToPickerRef = useRef<AddressPickerHandle>(null);

  const jobs: JobForDialog[] = (jobsData?.allJobs?.nodes || []).filter((j: JobForDialog) => !j.archivedAt);
  const selectedJob = jobs.find((j) => j.id === parseInt(String(formData.jobId), 10));
  const billToAddresses = selectedJob?.customerByCustomerId.addressesByCustomerId.nodes || [];
  const shipToAddresses = selectedJob
    ? dedupeAddresses([
        ...(selectedJob.addressByAddressId ? [selectedJob.addressByAddressId] : []),
        ...billToAddresses,
      ])
    : [];

  const handleSubmit = async () => {
    if (!formData.name.trim()) { setError('Quote name is required'); return; }
    if (!formData.jobId) { setError('Please select a job'); return; }
    if (!user?.id) { setError('User ID not found'); return; }
    setLoading(true);
    setError(null);
    try {
      const billToAddressId = await billToPickerRef.current?.commit() ?? formData.billToAddressId;
      const shipToAddressId = await shipToPickerRef.current?.commit() ?? formData.shipToAddressId;
      const result = await createDraftQuote({
        variables: {
          domainId: user.domain_id,
          jobId: parseInt(String(formData.jobId), 10),
          name: formData.name,
          billToAddressId: billToAddressId || null,
          shipToAddressId: shipToAddressId || null,
          notes: formData.notes || null,
          createdBy: user.id,
        },
      });
      const quoteId = result.data?.createDraftQuote?.draftQuote?.id;
      if (quoteId) onComplete({ quoteId });
    } catch (err: any) {
      setError(err.message || 'Failed to create quote');
    } finally {
      setLoading(false);
    }
  };

  const handleJobChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const jobId = e.target.value;
    const job = jobs.find((j) => j.id === parseInt(jobId, 10));
    setFormData({
      ...formData,
      jobId,
      billToAddressId: '',
      shipToAddressId: job?.addressByAddressId?.id ?? '',
    });
  };

  const createAddressForCustomer = async (customerId: number, fields: { street: string; city: string; state: string; zip: string }) => {
    const result = await createAddress({
      variables: {
        customerId,
        domainId: user!.domain_id,
        street: fields.street,
        city: fields.city,
        state: fields.state,
        zip: fields.zip,
        addressType: 'primary',
      },
      refetchQueries: ['AllJobsWithAddresses'],
    });
    return result.data.createAddress.address;
  };

  return (
    <div style={dialogOverlayStyle}>
      <div style={dialogStyle}>
        <div style={dialogHeaderStyle}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#111827', fontWeight: 700 }}>New Quote</h2>
          <button style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }} onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div style={dialogContentStyle}>
          {error && <div style={errorStyle}>{error}</div>}

          <div style={formGroupStyle}>
            <label style={labelStyle}>Quote Name <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              type="text"
              style={inputStyle}
              placeholder="e.g., Master Bedroom, Living Room"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={loading}
            />
          </div>

          {!initialJobId && (
            <div style={formGroupStyle}>
              <label style={labelStyle}>Job <span style={{ color: '#dc2626' }}>*</span></label>
              <select style={inputStyle} value={formData.jobId} onChange={handleJobChange} disabled={loading}>
                <option value="">Select a job...</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {job.customerByCustomerId.name} — {job.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {selectedJob && (
            <>
              <div style={formGroupStyle}>
                <AddressPicker
                  ref={billToPickerRef}
                  label="Bill-To Address (Optional)"
                  addresses={billToAddresses}
                  value={formData.billToAddressId}
                  onChange={(id) => setFormData({ ...formData, billToAddressId: id })}
                  disabled={loading}
                  onCreateAddress={(fields) => createAddressForCustomer(selectedJob.customerByCustomerId.id, fields)}
                />
              </div>
              <div style={formGroupStyle}>
                <AddressPicker
                  ref={shipToPickerRef}
                  label="Ship-To Address (Optional)"
                  addresses={shipToAddresses}
                  value={formData.shipToAddressId}
                  onChange={(id) => setFormData({ ...formData, shipToAddressId: id })}
                  disabled={loading}
                  onCreateAddress={(fields) => createAddressForCustomer(selectedJob.customerByCustomerId.id, fields)}
                />
              </div>
            </>
          )}

          <div style={formGroupStyle}>
            <label style={labelStyle}>Notes (Optional)</label>
            <textarea
              style={textareaStyle}
              placeholder="Add any notes about this quote..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              disabled={loading}
            />
          </div>
        </div>

        <div style={dialogFooterStyle}>
          <button style={cancelButtonStyle} onClick={onClose} disabled={loading}>Cancel</button>
          <button style={submitButtonStyle} onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : 'Save + Add Objects'}
          </button>
        </div>
      </div>
    </div>
  );
}
